/**
 * The operator's editor for a room's settings, and for the defaults a host opens new rooms with.
 * One component for both: the two are the same five knobs, and the only thing that differs is
 * which mutation the patch is handed to.
 *
 * Validation is the shared zod schemas rather than bounds restated here, in two steps, because
 * the server does the same two things in two places:
 *
 *  - the *merged* settings are checked with `roomSettingsSchema`, which is what the host
 *    re-validates them with — that is the only check that can see a contradiction between a
 *    field being changed and one that is staying put (lowering `maxPlayers` under an untouched
 *    `minPlayers`);
 *  - the *diff* is checked with `roomSettingsPatchSchema`, which is exactly what the admin
 *    router parses, so nothing can leave here that the procedure's own input would reject.
 *
 * Only changed fields are sent. An operator who opens the dialog and changes one number means
 * that one number, and a full object would silently re-assert the other four against whatever
 * the room has drifted to since the last poll.
 *
 * ## The world size, and why it is treated like a capacity knob
 *
 * A room's walls are settings too now, and they get `CapacityDialog`'s treatment rather than a
 * plain number field: the safe range is printed under each box, `worldSizeWarnings` — the same
 * function the admin router runs before it accepts — turns a value outside it into the sentences
 * an operator reads, and saving anyway needs the checkbox. Above MAX_WORLD_SIZE the schema
 * refuses outright and the confirmation cannot reach it.
 *
 * They are drawn only where they can be honoured (`worldSizeEditable`). A room that is already
 * running was built inside its walls — the spikes, the food and every player in it were placed
 * against them — so `liveRoomSettingsPatchSchema` refuses to move them and this dialog does not
 * offer to. The declaration on the roster is where a room's size is chosen.
 *
 * ## The mode, which is drawn wherever the walls are
 *
 * A room's mode is the walls' case rather than `access`': it is fixed at construction, and
 * `liveRoomSettingsPatchSchema` refuses it on a running room with a sentence of its own. So it is
 * offered exactly where it can be honoured (`modeEditable`) — on a declaration, and on the
 * defaults a host opens its *next* rooms with — and on the live-room editor it is not drawn at
 * all, because a control that can never take effect is a button that lies. The refusal is still
 * the schema's, not this form's: nothing here restates the rule, it only declines to offer it.
 *
 * ## Access rules, which ARE drawn everywhere
 *
 * The skill band and the device restriction are the opposite case, and they are offered on every
 * one of the four editors for that reason: they are a rule about the *door*, read where a player
 * is placed and where their ticket is signed, so narrowing them on a running room changes who
 * comes in next and evicts nobody. There is no confirmation behind them because there is no
 * measurement to be outside of — what there is instead is the sentence under the fields saying
 * that a room nobody can enter is a room nobody can enter.
 *
 * ## The layout, which is a fixed box with a scrolling middle
 *
 * This form has grown a field group at a time — the walls, then the access rules, then the mode —
 * and each was added to the bottom of one 32rem column. Declaring a room was the shape that
 * finally did not fit: on a laptop the dialog ran off the top *and* the bottom of the window, so
 * the room's own Name was above the viewport and the Declare button was below it, and there was
 * no scrollbar to reach because the panel had no bounded height to scroll inside.
 *
 * So the modal is wide (`max-w-5xl`), the knobs are two columns split by what an operator is
 * deciding rather than by height, and the *middle* scrolls: `scrollable` puts the field groups in
 * a `max-h` region and leaves the warnings, the server's refusal and the buttons pinned beneath
 * it, because those are the three things somebody has to be able to reach to finish the edit.
 *
 * **A `max-height` and not a height**, which is where this parts company with `InstructionsDialog`
 * — that page is a fixed box because its nav has to stay put beside a body of arbitrary length,
 * and this one has four shapes (a declaration, a live room with neither its mode nor its walls,
 * a host's defaults, the Workers tab's inline copy). A fixed height would leave the shortest of
 * them rattling around inside a tall empty box. It also means the height is only bounded when
 * there is something to bound it against: the inline form takes `scrollable: false` and is a
 * plain block in a page that already scrolls.
 */

import {
  BOT_TIER_ORDER,
  DEVICE_RULE_LABELS,
  MAX_DEATHMATCH_ROUND_SECONDS,
  ROOM_DEVICE_RULES,
  ROOM_MODE_DESCRIPTIONS,
  ROOM_MODE_LABELS,
  ROOM_MODES,
  SAFE_WORLD_SIZE,
  SKILL_BANDS,
  SKILL_MAX,
  SKILL_MIN,
  worldSizeWarnings,
} from '@chompdome/shared/constants';
import { roomSettingsPatchSchema, roomSettingsSchema } from '@chompdome/shared/schemas';
import type { BotSkillTier, RoomDeviceRule, RoomMode, RoomSettings } from '@chompdome/shared/types';
import { type ReactNode, useEffect, useState } from 'react';
import type { z } from 'zod';
import { NumberField } from '@/components/admin/number-field';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export type RoomSettingsPatch = z.infer<typeof roomSettingsPatchSchema>;

/**
 * The form itself, without the modal around it.
 *
 * Exported because the console has two shapes for the same edit: a dialog (the operations tab,
 * the roster) and an INLINE panel (the Workers tab, where a room expands under the worker it is
 * assigned to). Those are one component with one set of rules — a second copy of the five knobs
 * and the two schemas behind them is exactly the drift this file exists to prevent.
 */
export interface SettingsFormProps {
  /**
   * Only ever read to decide when to re-seed the draft. The dialog passes its own openness;
   * an inline form is mounted when it is wanted, so it defaults to true.
   */
  open?: boolean;
  /** What the room (or host) has now — the base every diff is taken against. */
  settings: RoomSettings;
  /** The server's refusal, if it refused; shown verbatim beside the form. */
  serverError: string | null;
  pending: boolean;
  /**
   * `confirmUnsafe` is the operator having read `worldSizeWarnings` and ticked the box; every
   * mutation that takes these settings takes it, and re-runs the same judgement server-side.
   */
  onSubmit: (patch: RoomSettingsPatch, confirmUnsafe: boolean) => void;
  onClose: () => void;
  /**
   * Draw the world-size fields. False for a room that is already running, whose walls cannot be
   * moved — see the block above. Defaults to true, which is the roster and the host defaults.
   */
  worldSizeEditable?: boolean;
  /**
   * Draw the mode selector. False for a room that is already running, for the walls' reason.
   *
   * A second prop rather than a second reading of `worldSizeEditable`, even though every call
   * site answers them the same way today: they are two knobs refused by two different sentences
   * in `liveRoomSettingsPatchSchema`, and one of them may yet become editable without the other.
   */
  modeEditable?: boolean;
  /**
   * Fields belonging to whatever is being edited rather than to the five knobs — a declared
   * room's static name and its owning host, which the roster panel owns the state of.
   *
   * A slot rather than more props because the knobs below are the only thing this component
   * knows how to validate: a second copy of the settings form inside the roster panel is
   * exactly the drift `roomSettingsSchema` exists to prevent.
   */
  extraFields?: ReactNode;
  /** Defaults to "Save settings"; the roster's create dialog says what it is really doing. */
  submitLabel?: string;
  /** Defaults to "Cancel"; an inline form is dismissed rather than closed. */
  cancelLabel?: string;
  /**
   * Hold the knobs in a scroll region of their own, with the buttons pinned under it.
   *
   * **True for a modal and false for the inline form, which is the difference between having a
   * viewport to fit inside and being in a page that already scrolls.** A dialog is centred in
   * the window: a form taller than the screen runs off the top *and* the bottom, taking the
   * header and the Save button with it, which is a form nobody can submit without a scrollbar
   * they cannot reach. The Workers tab's copy is expanded inline in a page that scrolls
   * perfectly well already, and a scroll region nested inside a scrolling page is the thing that
   * makes a long form hard to use rather than the thing that fixes one.
   *
   * It is a `max-height` rather than a height, so the shorter shapes of this form — the live
   * room's editor draws neither the mode nor the walls — are their own size instead of a short
   * form rattling around inside a tall box.
   */
  scrollable?: boolean;
}

export interface SettingsDialogProps extends SettingsFormProps {
  open: boolean;
  title: string;
  description: string;
}

const TIER_LABELS: Record<BotSkillTier, string> = {
  novice: 'Novice',
  casual: 'Casual',
  skilled: 'Skilled',
  expert: 'Expert',
};

/** Numbers are held as typed text so a half-typed field is not silently coerced to 0. */
interface Draft {
  mode: RoomMode;
  roundSeconds: string;
  maxPlayers: string;
  botsEnabled: boolean;
  minPlayers: string;
  maxBots: string;
  tierWeights: Record<BotSkillTier, string>;
  minSkill: string;
  maxSkill: string;
  devices: RoomDeviceRule;
  worldWidth: string;
  worldHeight: string;
}

function toDraft(settings: RoomSettings): Draft {
  return {
    mode: settings.mode,
    roundSeconds: String(settings.roundSeconds),
    maxPlayers: String(settings.maxPlayers),
    botsEnabled: settings.botsEnabled,
    minPlayers: String(settings.minPlayers),
    maxBots: String(settings.maxBots),
    tierWeights: {
      novice: String(settings.tierWeights.novice),
      casual: String(settings.tierWeights.casual),
      skilled: String(settings.tierWeights.skilled),
      expert: String(settings.tierWeights.expert),
    },
    minSkill: String(settings.access.minSkill),
    maxSkill: String(settings.access.maxSkill),
    devices: settings.access.devices,
    worldWidth: String(settings.worldWidth),
    worldHeight: String(settings.worldHeight),
  };
}

/** Blank becomes NaN rather than 0, so the schema rejects it instead of accepting an empty box. */
function toNumber(raw: string): number {
  return raw.trim() === '' ? Number.NaN : Number(raw);
}

function fromDraft(draft: Draft): Record<string, unknown> {
  return {
    mode: draft.mode,
    roundSeconds: toNumber(draft.roundSeconds),
    maxPlayers: toNumber(draft.maxPlayers),
    botsEnabled: draft.botsEnabled,
    minPlayers: toNumber(draft.minPlayers),
    maxBots: toNumber(draft.maxBots),
    tierWeights: {
      novice: toNumber(draft.tierWeights.novice),
      casual: toNumber(draft.tierWeights.casual),
      skilled: toNumber(draft.tierWeights.skilled),
      expert: toNumber(draft.tierWeights.expert),
    },
    access: {
      minSkill: toNumber(draft.minSkill),
      maxSkill: toNumber(draft.maxSkill),
      devices: draft.devices,
    },
    worldWidth: toNumber(draft.worldWidth),
    worldHeight: toNumber(draft.worldHeight),
  };
}

function sameWeights(a: RoomSettings['tierWeights'], b: RoomSettings['tierWeights']): boolean {
  return BOT_TIER_ORDER.every((tier) => a[tier] === b[tier]);
}

/**
 * Access rules are compared — and sent — as a WHOLE, because that is how the patch schema reads
 * them: a band is only meaningful as a pair, so half of one is not a diff anybody can apply.
 * `tierWeights` is under the same rule for the same reason.
 */
function sameAccess(a: RoomSettings['access'], b: RoomSettings['access']): boolean {
  return a.minSkill === b.minSkill && a.maxSkill === b.maxSkill && a.devices === b.devices;
}

function diff(before: RoomSettings, after: RoomSettings): RoomSettingsPatch {
  const patch: RoomSettingsPatch = {};
  // Only when it moved, which is what keeps a live room's editor honest without a second rule:
  // that form never draws the selector, so its draft holds whatever the room already has and the
  // key is never in the patch at all.
  if (after.mode !== before.mode) patch.mode = after.mode;
  if (after.roundSeconds !== before.roundSeconds) patch.roundSeconds = after.roundSeconds;
  if (after.maxPlayers !== before.maxPlayers) patch.maxPlayers = after.maxPlayers;
  if (after.botsEnabled !== before.botsEnabled) patch.botsEnabled = after.botsEnabled;
  if (after.minPlayers !== before.minPlayers) patch.minPlayers = after.minPlayers;
  if (after.maxBots !== before.maxBots) patch.maxBots = after.maxBots;
  if (!sameWeights(after.tierWeights, before.tierWeights)) patch.tierWeights = after.tierWeights;
  if (!sameAccess(after.access, before.access)) patch.access = after.access;
  if (after.worldWidth !== before.worldWidth) patch.worldWidth = after.worldWidth;
  if (after.worldHeight !== before.worldHeight) patch.worldHeight = after.worldHeight;
  return patch;
}

/**
 * The skill scale in the words printed under each of its two boxes.
 *
 * The band names rather than the raw numbers, because an operator setting "50" has no scale to
 * read it against and `SKILL_BANDS` is the one this deployment actually uses.
 */
const SKILL_HINT = `${SKILL_MIN}–${SKILL_MAX}. ${SKILL_BANDS.map(
  (band) => `${band.name} ${band.min}+`,
).join(', ')}.`;

/**
 * What the round limit does, in the words printed under its box.
 *
 * The default and the ceiling are the shared constants rather than figures typed here, so a card
 * that retunes either moves this sentence with it.
 */
const ROUND_LIMIT_HINT =
  `0 for no limit. Up to ${MAX_DEATHMATCH_ROUND_SECONDS.toLocaleString('en-US')}. ` +
  'When it expires the biggest player wins the round. Applies to the next round, not a running one.';

/** The measured-safe band, in the words printed under each of the two world-size boxes. */
const WORLD_SIZE_HINT =
  `Measured: ${SAFE_WORLD_SIZE.min.toLocaleString('en-US')}–` +
  `${SAFE_WORLD_SIZE.max.toLocaleString('en-US')} units. Pellets and spikes scale with the area.`;

/** Issues keyed by `path.join('.')`, which is how each field below looks its own message up. */
function issuesByPath(error: z.ZodError): Record<string, string> {
  const messages: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join('.');
    messages[key] ??= issue.message;
  }
  return messages;
}

/**
 * The five knobs (plus the walls, where they may be moved), the two schemas that judge them and
 * the confirmation an unmeasured world size needs — with no modal around any of it.
 */
export function SettingsForm({
  open = true,
  settings,
  serverError,
  pending,
  onSubmit,
  onClose,
  worldSizeEditable = true,
  modeEditable = true,
  extraFields,
  submitLabel = 'Save settings',
  cancelLabel = 'Cancel',
  scrollable = false,
}: SettingsFormProps) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);

  // Re-seed when the dialog is pointed at a different room, and drop the previous attempt's
  // complaints and its confirmation with it — a tick must never carry over to a fresh value.
  useEffect(() => {
    if (!open) return;
    setDraft(toDraft(settings));
    setErrors({});
    setConfirmed(false);
  }, [open, settings]);

  // Judged live on what has been typed, on the same two arguments the admin router judges on:
  // the diff decides which sides are warned about — a knob the operator did not touch must not
  // produce a box to tick — and the base is what makes the *area* judgeable when only one side
  // moved. A dialog whose walls are not editable can never produce one.
  const typed = roomSettingsSchema.safeParse(fromDraft(draft));
  const warnings =
    worldSizeEditable && typed.success
      ? worldSizeWarnings(diff(settings, typed.data), settings)
      : [];
  const blocked = warnings.length > 0 && !confirmed;

  const submit = () => {
    const whole = roomSettingsSchema.safeParse(fromDraft(draft));
    if (!whole.success) {
      setErrors(issuesByPath(whole.error));
      return;
    }

    const patch = roomSettingsPatchSchema.safeParse(diff(settings, whole.data));
    if (!patch.success) {
      setErrors(issuesByPath(patch.error));
      return;
    }

    setErrors({});
    onSubmit(patch.data, warnings.length > 0);
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {/* The knobs, and the only part of this form that is ever allowed to scroll. Everything
          below it — the warnings, the server's refusal and the buttons — stays pinned, because
          those are the three things an operator has to be able to reach to finish the edit. */}
      <div className={cn('space-y-4', scrollable && 'max-h-[62dvh] overflow-y-auto pr-1')}>
        {extraFields}

        {/* TWO COLUMNS RATHER THAN ONE TALL STACK. These are seven small field groups, and a
            single column of them is taller than a laptop — which put the dialog's own header
            off the top of the screen and its Save button off the bottom. The split is by what
            an operator is deciding rather than by height: the left column is what the room IS
            — how it plays, how many seats, how big — and the right is who is let into it and
            what fills it. Each group keeps its own inner grid, so one row of this dialog
            carries up to six fields; below `lg` the columns stack and it is the form it has
            always been. */}
        <div className="grid grid-cols-1 items-start gap-x-8 gap-y-4 lg:grid-cols-2">
          <div className="space-y-4">
            {!modeEditable && (
              <FixedField
                label="Mode"
                value={ROOM_MODE_LABELS[draft.mode]}
                reason="Decided when this room opened — its match loop and the way it enrolled the people in it both come from it. Change the declaration on Room roster and restart the room."
              />
            )}
            {modeEditable && (
              <div className="space-y-1.5">
                <label htmlFor="settings-mode" className="text-sm font-medium">
                  Mode
                </label>
                <select
                  id="settings-mode"
                  value={draft.mode}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, mode: event.target.value as RoomMode }))
                  }
                  className="h-9 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-1 text-sm"
                >
                  {ROOM_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {ROOM_MODE_LABELS[mode]}
                    </option>
                  ))}
                </select>
                {/* The device rule's caption, one knob up: what this actually does, in the words
                    the shared constant carries, so the sentence an operator reads here and the
                    sentence any later surface draws beside a badge are the same one. */}
                <p className="text-xs text-muted-foreground">
                  {ROOM_MODE_DESCRIPTIONS[draft.mode]} Fixed once the room opens — it applies the
                  next time it is started.
                </p>
              </div>
            )}

            {/* **DRAWN ONLY FOR A DEATHMATCH ROOM, because it is read only by one.** A classic
                room never starts a round, so the field would be a control that can never take
                effect — the rejected-photo tile's rule, and the same one that keeps the mode
                selector off the live-room editor. It IS offered on a running deathmatch room,
                unlike the mode: it is read once at the start of a round, so a change decides the
                next one and cannot move a deadline anybody is already racing against. */}
            {draft.mode === 'deathmatch' && (
              <NumberField
                id="settings-round-seconds"
                label="Round limit (seconds)"
                hint={ROUND_LIMIT_HINT}
                value={draft.roundSeconds}
                error={errors.roundSeconds}
                onChange={(roundSeconds) => setDraft((current) => ({ ...current, roundSeconds }))}
              />
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <NumberField
                id="settings-max-players"
                label="Max players"
                value={draft.maxPlayers}
                error={errors.maxPlayers}
                onChange={(maxPlayers) => setDraft((current) => ({ ...current, maxPlayers }))}
              />
              <NumberField
                id="settings-min-players"
                label="Min players"
                hint="Bots top the room up to this total."
                value={draft.minPlayers}
                error={errors.minPlayers}
                onChange={(minPlayers) => setDraft((current) => ({ ...current, minPlayers }))}
              />
              <NumberField
                id="settings-max-bots"
                label="Max bots"
                value={draft.maxBots}
                error={errors.maxBots}
                onChange={(maxBots) => setDraft((current) => ({ ...current, maxBots }))}
              />
            </div>

            {!worldSizeEditable && (
              <FixedField
                label="World size"
                value={`${draft.worldWidth} x ${draft.worldHeight}`}
                reason="The room was built inside these walls and every body in it was placed against them. Change the declaration on Room roster and restart the room."
              />
            )}
            {worldSizeEditable && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberField
                  id="settings-world-width"
                  label="World width"
                  hint={WORLD_SIZE_HINT}
                  value={draft.worldWidth}
                  error={errors.worldWidth}
                  onChange={(worldWidth) => setDraft((current) => ({ ...current, worldWidth }))}
                />
                <NumberField
                  id="settings-world-height"
                  label="World height"
                  hint={WORLD_SIZE_HINT}
                  value={draft.worldHeight}
                  error={errors.worldHeight}
                  onChange={(worldHeight) => setDraft((current) => ({ ...current, worldHeight }))}
                />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Who may enter</legend>
              <p className="text-xs text-muted-foreground">
                Checked when a player is placed and when their ticket is signed — never in the
                browser. Narrowing this on a room that is already running changes who joins next and
                removes nobody who is already inside. {SKILL_MIN}–{SKILL_MAX} and “
                {DEVICE_RULE_LABELS.any}” let everybody in.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <NumberField
                  id="settings-min-skill"
                  label="Lowest skill"
                  hint={SKILL_HINT}
                  value={draft.minSkill}
                  error={errors['access.minSkill']}
                  onChange={(minSkill) => setDraft((current) => ({ ...current, minSkill }))}
                />
                <NumberField
                  id="settings-max-skill"
                  label="Highest skill"
                  hint={SKILL_HINT}
                  value={draft.maxSkill}
                  error={errors['access.maxSkill']}
                  onChange={(maxSkill) => setDraft((current) => ({ ...current, maxSkill }))}
                />
                <div className="space-y-1.5">
                  <label htmlFor="settings-devices" className="text-sm font-medium">
                    Devices
                  </label>
                  <select
                    id="settings-devices"
                    value={draft.devices}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        devices: event.target.value as RoomDeviceRule,
                      }))
                    }
                    className="h-9 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-1 text-sm"
                  >
                    {ROOM_DEVICE_RULES.map((rule) => (
                      <option key={rule} value={rule}>
                        {DEVICE_RULE_LABELS[rule]}
                      </option>
                    ))}
                  </select>
                  {/* Said out loud where the knob is, because it is the one thing about this rule
                      an operator cannot see from the console: the platform a request declares is
                      not a fact the server can verify the way it verifies a country. */}
                  <p className="text-xs text-muted-foreground">
                    Read from the client the request came from. Sorts an honest audience; it is not
                    a barrier somebody determined cannot get past.
                  </p>
                </div>
              </div>
              {errors.access && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.access}
                </p>
              )}
            </fieldset>

            {/* The switch sits INSIDE the tier mix rather than on a line of its own above it,
                which is the one grouping this layout changed: the weights are meaningless while
                bots are off, so an operator reading the mix is reading the answer to a question
                the checkbox asks. Alone in a two-column grid it was also a whole row spent on
                one checkbox. */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Bots</legend>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={draft.botsEnabled}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, botsEnabled: event.target.checked }))
                  }
                  className="h-4 w-4 cursor-pointer accent-primary"
                />
                Bots enabled
              </label>
              <p className="text-xs text-muted-foreground">
                Relative spawn weights. They are read as a share of their own sum, so at least one
                has to be above zero.
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {BOT_TIER_ORDER.map((tier) => (
                  <NumberField
                    key={tier}
                    id={`settings-tier-${tier}`}
                    label={TIER_LABELS[tier]}
                    value={draft.tierWeights[tier]}
                    error={errors[`tierWeights.${tier}`]}
                    onChange={(weight) =>
                      setDraft((current) => ({
                        ...current,
                        tierWeights: { ...current.tierWeights, [tier]: weight },
                      }))
                    }
                  />
                ))}
              </div>
              {errors.tierWeights && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.tierWeights}
                </p>
              )}
            </fieldset>
          </div>
        </div>
      </div>

      {warnings.length > 0 && (
        <div
          className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3"
          role="alert"
        >
          <p className="text-sm font-semibold text-destructive">
            Outside the range the field was tuned at
          </p>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          <label className="flex items-start gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              className="mt-0.5 h-4 w-4 cursor-pointer accent-destructive"
            />
            Save these values anyway
          </label>
        </div>
      )}

      {serverError && (
        <p className="text-sm text-destructive" role="alert">
          {serverError}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          {cancelLabel}
        </Button>
        <Button type="submit" size="sm" disabled={pending || blocked}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/**
 * The same form, in the modal three of the console's four editors want it in.
 *
 * **Wide, and clipped.** `max-w-lg` was a single 32rem column holding seven field groups, which
 * on a laptop ran off the top and the bottom of the window at once — the room's own name above
 * the viewport and the Declare button below it, with no scrollbar reachable because the panel
 * had no height to scroll inside. `max-w-5xl` is what makes two columns worth having; the panel
 * is `overflow-hidden` so the scroll region inside it is clipped by the card's own rounded
 * corners rather than painting over them; and `scrollable` is what puts the knobs in that region
 * and pins the buttons under it. The dialog's own header and its close button are outside the
 * children, so they were never the part that scrolled and they stay put for free.
 *
 * The shared `Dialog` is untouched, which it has to be: every other modal in this app is a short
 * block that wants to be exactly as tall as it is.
 */
export function SettingsDialog({ open, title, description, ...form }: SettingsDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={form.onClose}
      title={title}
      description={description}
      className="max-w-5xl overflow-hidden"
    >
      <SettingsForm {...form} open={open} scrollable />
    </Dialog>
  );
}

/**
 * A setting this editor cannot change, drawn as a value and a reason rather than left out.
 *
 * **AN ABSENT FIELD IS WHAT MADE THE FOUR EDITORS LOOK LIKE DIFFERENT FORMS.** They are one
 * component and always have been (`SettingsForm`); what differs is that
 * `liveRoomSettingsPatchSchema` refuses a running room its mode and its walls, so those two were
 * simply not rendered — and the console read as though the Rooms & Processes tab had a smaller,
 * older form with no deathmatch option in it. Andy: "the rooms and processes admin tab for room
 * settings is different from other room settings (and doesn't have deathmatch option). Make sure
 * they're all using same settings."
 *
 * **IT IS A READOUT AND NOT A DISABLED CONTROL**, which is the rejected-photo tile's rule: a
 * control that can never take effect is a button that lies, and a greyed-out `<select>` invites a
 * click that will do nothing. What an operator is owed instead is the value, the reason it is
 * fixed, and where to go to change it — so every editor now has a line for every setting, and
 * the ones that are fixed say so.
 */
function FixedField({ label, value, reason }: { label: string; value: string; reason: string }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{label}</p>
      <p
        data-testid={`settings-fixed-${label.toLowerCase().replace(/ /g, '-')}`}
        className="flex h-9 items-center rounded-md border border-dashed border-input bg-muted/40 px-3 text-sm text-muted-foreground"
      >
        {value}
      </p>
      <p className="text-xs text-muted-foreground">{reason}</p>
    </div>
  );
}
